// Lemon.js checkout-overlay integration.
//
// Loads Lemon Squeezy's lemon.js once, exposes `openOverlay(url)` to open the
// checkout overlay programmatically, and forwards the `Checkout.Success` event
// to an optional callback. The overlay keeps the buyer on apply-edge.com while
// the checkout itself runs inside an LS-hosted iframe (PCI scope stays with
// Lemon Squeezy).
//
// Progressive enhancement: callers keep a real checkout `href` and only
// `preventDefault()` + `openOverlay()` on click, so if lemon.js fails to load
// the click falls back to a full-page redirect. Granting entitlements is the
// billing webhook's job — the client `Checkout.Success` event is used for UX
// only (route to the return page), never to unlock paid features.
import { useCallback, useEffect, useRef } from "react";

import { axios } from "@/client/libs/axios";
import { trackMilestone } from "@/client/services/analytics/google-analytics";

const LEMON_JS_SRC = "https://assets.lemonsqueezy.com/lemon.js";

type LemonSqueezyEvent = { event?: string };

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    createLemonSqueezy?: () => void;
    LemonSqueezy?: {
      Setup: (options: { eventHandler: (event: LemonSqueezyEvent) => void }) => void;
      Url: { Open: (url: string) => void; Close: () => void };
      Refresh: () => void;
    };
  }
}

let loadPromise: Promise<void> | null = null;

const loadLemonJs = (): Promise<void> => {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.LemonSqueezy) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${LEMON_JS_SRC}"]`);
    const onError = () => {
      reject(new Error("lemon.js failed to load"));
    };
    if (existing) {
      existing.addEventListener("load", () => {
        resolve();
      });
      existing.addEventListener("error", onError);
      return;
    }
    const script = document.createElement("script");
    script.src = LEMON_JS_SRC;
    script.defer = true;
    script.addEventListener("load", () => {
      resolve();
    });
    script.addEventListener("error", onError);
    document.head.append(script);
  });

  return loadPromise;
};

/**
 * Append the LS `embed=1` flag so the checkout renders as an on-site overlay
 * instead of a full-page redirect. Returns the URL unchanged if it can't be
 * parsed (the anchor still works as a plain redirect).
 */
export const withCheckoutOverlay = (checkoutUrl: string): string => {
  try {
    const url = new URL(checkoutUrl);
    url.searchParams.set("embed", "1");
    return url.toString();
  } catch {
    return checkoutUrl;
  }
};

/**
 * `window.LemonSqueezy.Setup()` registers a SINGLE global event handler — the
 * last caller wins. With several mounted consumers (trial banner + sidebar
 * upgrade + dashboard banner…) every earlier `onSuccess` was silently dropped,
 * so a buyer could complete a checkout and never reach /billing/return. Setup
 * once, module-level, and fan the event out to every live subscriber instead.
 */
const successSubscribers = new Set<{ current?: () => void }>();
let isSetup = false;

const ensureSetup = () => {
  if (isSetup || !window.LemonSqueezy?.Setup) return;
  isSetup = true;
  window.LemonSqueezy.Setup({
    eventHandler: (event) => {
      if (event.event !== "Checkout.Success") return;
      for (const subscriber of successSubscribers) subscriber.current?.();
    },
  });
};

type UseLemonSqueezyOverlayOptions = {
  /** Fired on the LS `Checkout.Success` event (UX only — not an entitlement). */
  onSuccess?: () => void;
};

type UseLemonSqueezyOverlayResult = {
  /** Open a checkout overlay programmatically (for button-driven flows). */
  openOverlay: (checkoutUrl: string, analytics?: CheckoutAnalytics) => void;
  openHosted: (checkoutUrl: string, analytics?: CheckoutAnalytics) => Promise<boolean>;
  isHandoffInFlight?: () => boolean;
};

/**
 * Funnel props for `checkout_started` (spec 048 FR-017). `surface` is where the
 * user clicked, `plan` and `interval` are what was offered. The server adds
 * `status` from the subscription row, so the client does not send it.
 */
type CheckoutAnalytics = {
  surface: string;
  plan: "PRO" | "ULTIMATE";
  interval: "weekly" | "monthly" | "yearly";
  attempt_id?: string;
};

/** Why the embedded overlay could not open; recorded as `checkout_open_result.code`. */
type OverlayFailure = "lemon_js_load_failed" | "lemon_unavailable" | "overlay_open_threw";

/** Longest the hosted fallback waits for the outcome report before navigating anyway. */
const REPORT_BEFORE_NAVIGATION_MS = 800;

type AnalyticsPayload = { event: "checkout_started" | "checkout_open_result"; props?: object };

/** Beacon survives a page unload; a rejected beacon falls back to the existing POST. */
const reportForNavigation = async (payload: AnalyticsPayload): Promise<void> => {
  try {
    if (
      typeof navigator.sendBeacon === "function" &&
      navigator.sendBeacon(
        "/api/analytics/track",
        new Blob([JSON.stringify(payload)], { type: "application/json" }),
      )
    ) {
      return;
    }
  } catch {
    // A throwing beacon is equivalent to an unavailable one.
  }
  try {
    await axios.post("/analytics/track", payload);
  } catch {
    // Best-effort telemetry must never affect checkout.
  }
};

/** Reporting the outcome must never affect the checkout itself: it never rejects. */
const reportOpenResult = async (
  analytics: CheckoutAnalytics | undefined,
  outcome: { method: "overlay" | "hosted" | "hosted_fallback"; ok: boolean; code?: string },
): Promise<void> => {
  if (!analytics) return;
  try {
    await axios.post("/analytics/track", {
      event: "checkout_open_result",
      props: { ...analytics, ...outcome },
    });
  } catch {
    // Best-effort telemetry: a failed report must never touch the checkout.
  }
};

/** Resolve when `work` settles or after `ms`, whichever is first. */
const settleWithin = async (work: Promise<unknown>, ms: number): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  await Promise.race([work, timeout]);
  clearTimeout(timer);
};

/** Try the embedded overlay; returns why it could not open, or null on success. */
const tryOverlay = async (checkoutUrl: string): Promise<OverlayFailure | null> => {
  try {
    await loadLemonJs();
  } catch {
    return "lemon_js_load_failed";
  }
  // lemon.js is third-party: tolerate a global that loaded without `Url`.
  const lemon = window.LemonSqueezy as Partial<NonNullable<Window["LemonSqueezy"]>> | undefined;
  const open = lemon?.Url?.Open;
  if (typeof open !== "function") return "lemon_unavailable";
  try {
    open(withCheckoutOverlay(checkoutUrl));
    return null;
  } catch {
    return "overlay_open_threw";
  }
};

/**
 * Open the checkout: overlay first, then the full hosted page in this tab.
 * Records which method worked so "opened a checkout" can be told apart from
 * "saw a payment form".
 */
const NAVIGATION_GUARD_MS = 4000;

/**
 * True from the click until the checkout has opened (overlay) or the tab has been
 * sent to the hosted page. Callers' own pending guards release as soon as the
 * bound URL arrives, which is BEFORE this async handoff ends, so a second click
 * in that window would start another checkout and a second navigation.
 */
let handoffInFlight = false;

/** Test seam: the guard is module state, so tests must start from a clean slate. */
export const resetCheckoutHandoff = (): void => {
  handoffInFlight = false;
};

const openCheckout = async (
  checkoutUrl: string,
  analytics: CheckoutAnalytics | undefined,
  startedReport: Promise<void>,
): Promise<void> => {
  const failure = await tryOverlay(checkoutUrl);
  if (!failure) {
    handoffInFlight = false;
    void reportOpenResult(analytics, { method: "overlay", ok: true });
    return;
  }
  // A page unload cancels in-flight requests, so both reports get a bounded
  // wait BEFORE the tab leaves if beacon was unavailable. `ok: true` means "navigating to the bound
  // hosted URL"; assigning `href` essentially never throws, but if it does a
  // correction with ok:false follows so a failed fallback is still visible.
  await settleWithin(
    Promise.all([
      startedReport,
      analytics
        ? reportForNavigation({
            event: "checkout_open_result",
            props: { ...analytics, method: "hosted_fallback", ok: true, code: failure },
          })
        : Promise.resolve(),
    ]),
    REPORT_BEFORE_NAVIGATION_MS,
  );
  try {
    window.location.href = checkoutUrl;
    // The page is leaving: keep ignoring clicks, but release if the navigation
    // never happens (blocked, or the page is restored from the back/forward cache).
    setTimeout(() => {
      handoffInFlight = false;
    }, NAVIGATION_GUARD_MS);
  } catch {
    handoffInFlight = false;
    void reportOpenResult(analytics, { method: "hosted_fallback", ok: false, code: failure });
  }
};

/** Navigate directly to the API-bound hosted page without loading Lemon.js. */
const navigateHosted = async (
  checkoutUrl: string,
  analytics: CheckoutAnalytics | undefined,
  startedReport: Promise<void>,
): Promise<boolean> => {
  await settleWithin(
    Promise.all([
      startedReport,
      analytics
        ? reportForNavigation({
            event: "checkout_open_result",
            props: { ...analytics, method: "hosted", ok: true },
          })
        : Promise.resolve(),
    ]),
    REPORT_BEFORE_NAVIGATION_MS,
  );
  try {
    window.location.href = checkoutUrl;
    setTimeout(() => {
      handoffInFlight = false;
    }, NAVIGATION_GUARD_MS);
    return true;
  } catch {
    handoffInFlight = false;
    void reportOpenResult(analytics, { method: "hosted", ok: false });
    return false;
  }
};

export const useLemonSqueezyOverlay = ({
  onSuccess,
}: UseLemonSqueezyOverlayOptions = {}): UseLemonSqueezyOverlayResult => {
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    let cancelled = false;
    successSubscribers.add(onSuccessRef);
    loadLemonJs()
      .then(() => {
        if (cancelled) return;
        // Initialise the LemonSqueezy global (required before Url.Open in a
        // SPA, where lemon.js loads after first paint).
        window.createLemonSqueezy?.();
        ensureSetup();
      })
      .catch(() => {
        // Overlay is optional; anchors fall back to their href redirect.
      });
    return () => {
      cancelled = true;
      // Without this, a `Checkout.Success` arriving after unmount would call a
      // stale `navigate` captured by a component that no longer exists.
      successSubscribers.delete(onSuccessRef);
    };
  }, []);

  const trackAndOpen = useCallback(
    (
      checkoutUrl: string,
      analytics: CheckoutAnalytics | undefined,
      mode: "overlay" | "hosted",
    ): Promise<boolean> | undefined => {
      if (handoffInFlight) return;
      handoffInFlight = true;
      // Legacy callers without an attempt ID still report the click here.
      // API callers already reported it at the guarded request step.
      const startedReport = analytics?.attempt_id
        ? Promise.resolve()
        : reportForNavigation({
            event: "checkout_started",
            ...(analytics ? { props: analytics } : {}),
          });
      trackMilestone("begin_checkout");

      // Read the URL at click time so cycle/price toggles always open the right
      // checkout. The API URL is bound to the exact variant in either mode.
      if (mode === "hosted") return navigateHosted(checkoutUrl, analytics, startedReport);
      else void openCheckout(checkoutUrl, analytics, startedReport);
    },
    [],
  );

  const openOverlay = useCallback(
    (url: string, analytics?: CheckoutAnalytics) => {
      void trackAndOpen(url, analytics, "overlay");
    },
    [trackAndOpen],
  );
  const openHosted = useCallback(
    (url: string, analytics?: CheckoutAnalytics) =>
      trackAndOpen(url, analytics, "hosted") ?? Promise.resolve(false),
    [trackAndOpen],
  );

  return { openOverlay, openHosted, isHandoffInFlight: () => handoffInFlight };
};
